import SrOnly from '@tvg/fuse-utils/lib/sr-only';
import { PLPQueryData, usePLPQuery } from 'client/api/plp-results';
import { EUserAction } from 'client/config';
import withPrefetch from 'client/hoc/withPrefetch';
import { usePLPNavigate } from 'client/hooks/usePLPNavigate';
import { SEOAnchorStyled } from 'client/pages/CheckboxGroup/SEOAnchorStyled/SEOAnchorStyled';
import { useDrawerState } from 'client/state/Drawer';
import { useMainState } from 'client/state/Main';
import { FacetType } from 'common/types/facet';
import React, { FormEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BaseChip from './Chip';
import { ChipListStyled, ChipsBox, ClearAllButtonStyled } from './ChipList.styled';
import { findChangedFacets, generateChangedFilterText } from './helpers';

const Chip = withPrefetch(BaseChip);
const SEOAnchor = withPrefetch(SEOAnchorStyled);

function ChipListComponent() {
  const { data } = usePLPQuery(
    useCallback((s: PLPQueryData) => ({ selectedFacets: s.selectedFacets, clearAllURL: s.links.clearAll }), []),
  );
  const plpurl = useMainState((s) => s.plpurl);
  const toggleShowViewProducts = useDrawerState((s) => s.toggleShowViewProducts);
  const plpNavigate = usePLPNavigate();

  const [recentlyChangedFilterText, setRecentlyChangedFilterText] = useState<string>('');
  const selectedFacetsPreviousState = useRef(data?.selectedFacets);

  const filteredFacets = useMemo(
    () =>
      Object.entries(data?.selectedFacets ?? {})
        .filter(
          ([facetId]) =>
            facetId !== FacetType.department && facetId !== FacetType.category && facetId !== plpurl.unfilteredFacet,
        )
        .flatMap(([facetId, values]) => values.map((v) => ({ ...v, facetId }))),
    [data, plpurl.unfilteredFacet],
  );

  useEffect(() => {
    const changedFacets = findChangedFacets(data?.selectedFacets || {}, selectedFacetsPreviousState.current || {});

    const changedFilterText = generateChangedFilterText(changedFacets);

    setTimeout(() => setRecentlyChangedFilterText(changedFilterText), 2000);

    selectedFacetsPreviousState.current = data?.selectedFacets;
  }, [data]);

  const handleClearAll = useCallback(
    (e: FormEvent<HTMLElement>) => {
      e.preventDefault();
      toggleShowViewProducts.on();
      plpNavigate({ facets: plpurl.facets?.filter((f) => f.id === plpurl.unfilteredFacet) }, EUserAction.FACET);
    },
    [plpNavigate, plpurl.facets, plpurl.unfilteredFacet, toggleShowViewProducts],
  );

  return (
    <ChipsBox>
      <ChipListStyled>
        {filteredFacets.map(({ id, name, url, facetId }) => (
          <Chip key={id + name} id={id} name={name} facetId={facetId} href={url} />
        ))}
      </ChipListStyled>
      <SrOnly tabIndex={-1} aria-live="polite">
        {recentlyChangedFilterText}
      </SrOnly>
      {!!filteredFacets.length && (
        <SEOAnchor href={data?.clearAllURL} className="forSEO" tabIndex={-1}>
          <ClearAllButtonStyled variant="tertiary" onClick={handleClearAll} data-testid="clearall-button">
            Clear All
          </ClearAllButtonStyled>
        </SEOAnchor>
      )}
    </ChipsBox>
  );
}

export const ChipList = memo(ChipListComponent);
